// =====================================================================
//  Import en CRÉATION d'un paquet dans un projet
//  ---------------------------------------------------------------------
//  Persiste immédiatement, comme le bouton « Ajouter » des listes :
//    1. le pivot (fr) avec son modèle de test,
//    2. ses traductions éventuelles,
//    3. la stratégie OTP éventuelle, rattachée au template créé.
//  Les écritures sont silencieuses (pas d'événement de liste) : c'est
//  l'appelant qui ouvre l'éditeur voulu une fois le tout créé, avec le
//  groupe i18n rechargé.
//  L'import en MISE À JOUR, lui, ne persiste rien : il pré-remplit
//  l'éditeur ouvert (TemplateEditor.ApplyPackage / OtpEditor.ApplyPackage).
// =====================================================================

import {IEasymailPackage} from "../../Model/Package/IEasymailPackage.js";
import {ITemplateModel} from "../../Model/Template/ITemplateModel.js";
import {IOtpStrategyModel} from "../../Model/Otp/OtpStrategyModel.js";
import {PackageMapper} from "./PackageMapper.js";
import {TemplateUpsert} from "../../Controller/Template/TemplateUpsert.js";
import {TemplateRead} from "../../Controller/Template/TemplateRead.js";
import {OtpUpsert} from "../../Controller/Otp/OtpUpsert.js";
import {NotificationHelper} from "../../Helper/NotificationHelper.js";

export interface IPackageCreateResult {
    /** Pivot rechargé (Datas + Translations), prêt à ouvrir dans TemplateEditor. */
    pivot: ITemplateModel;
    /** Stratégie créée, si le paquet en contenait une. */
    otpStrategy?: IOtpStrategyModel;
}

export class PackageImporter {

    /** Crée le contenu du paquet dans le projet ; undefined si le pivot n'a pas pu être créé. */
    public static Create = async (pkg: IEasymailPackage, fkProjet: string): Promise<IPackageCreateResult | undefined> => {
        const saved = await TemplateUpsert.Put(PackageMapper.ToNewPivot(pkg, fkProjet), true);
        if (!saved)
            return undefined; // erreurs déjà notifiées par TemplateUpsert

        const failures: Array<string> = [];
        for (const tr of pkg.translations ?? []) {
            const t = await TemplateUpsert.Put(PackageMapper.ToNewTranslation(tr, saved), true);
            if (!t)
                failures.push(`traduction ${tr.language.toUpperCase()}`);
        }

        let otpStrategy: IOtpStrategyModel | undefined;
        if (pkg.otpStrategy) {
            otpStrategy = await OtpUpsert.Upsert(PackageMapper.ToNewOtpStrategy(pkg.otpStrategy, fkProjet, saved.Primarykey));
            if (!otpStrategy)
                failures.push(`stratégie OTP « ${pkg.otpStrategy.name} »`);
        }

        if (failures.length)
            NotificationHelper.Instance.warning(
                `Template « ${saved.Nom} » créé, mais échec pour : ${failures.join(", ")}. Complétez à la main ou supprimez le template.`);

        return {pivot: await PackageImporter.ReadGroup(saved.Primarykey), otpStrategy};
    };

    /** Recharge un groupe i18n complet (pivot + Datas + Translations), comme TemplateListItem. */
    public static ReadGroup = async (pivotPrimarykey: string): Promise<ITemplateModel> => {
        const group = await TemplateRead.Read({
            Primarykey: pivotPrimarykey,
            AsNoTracking: true,
            IncludeDatas: true,
            IncludeTranslations: true,
        });
        const pivot = group.find(x => !x.FkParent) ?? group[0];
        pivot.Translations = group.filter(x => x.Primarykey !== pivot.Primarykey);
        return pivot;
    };

    /** Résumé « ce que va faire l'import » pour une création. */
    public static DescribeCreate = (pkg: IEasymailPackage): Array<string> => {
        const lines = [`Créer le template « ${pkg.template.name} » (${pkg.template.typeContent.toUpperCase()}, ${pkg.template.typeProcess ?? "mail"}) avec son modèle de test.`];
        const langs = (pkg.translations ?? []).map(t => t.language.toUpperCase());
        if (langs.length)
            lines.push(`Créer ${langs.length} traduction(s) : ${langs.join(", ")}.`);
        else
            lines.push("Aucune traduction : utilisez « Traduire » après vérification du français.");
        if (pkg.template.sender)
            lines.push(`Expéditeur par défaut : ${pkg.template.sender.name ? pkg.template.sender.name + " " : ""}<${pkg.template.sender.email}>.`);
        else
            lines.push("Pas d'expéditeur par défaut : à renseigner dans l'onglet Sujet si l'application n'en fournit pas.");
        if (pkg.otpStrategy)
            lines.push(`Créer la stratégie OTP « ${pkg.otpStrategy.name} » (${pkg.otpStrategy.pattern.length} caractères, ${pkg.otpStrategy.expirationMinutes} min, ${pkg.otpStrategy.maxAttempts} essais) utilisant ce template.`);
        lines.push("Le tout est enregistré immédiatement ; l'éditeur s'ouvre ensuite pour la prévisualisation.");
        return lines;
    };
}
